import { useEffect, useState } from "react";
import { SupabaseInstance } from "@/integrations/supabase";
import useAuth from "./useAuth";
import { ONE_MINUTE_TIMEOUT } from "@/constants/timeouts";
import { Logger } from "@/utils";
import type {
  FeatureFlagEvaluation,
  FeatureFlagKey,
} from "@/types/featureFlags";

// Cache local para evitar queries repetidas
const flagCache = new Map<string, { value: boolean; timestamp: number }>();
const CACHE_TTL = ONE_MINUTE_TIMEOUT; // 1 minuto

export function useFeatureFlag(
  flagKey: FeatureFlagKey | string,
): FeatureFlagEvaluation {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Si no hay usuario, por defecto el flag está deshabilitado
    // (A menos que sea un flag público, pero asumimos auth por ahora)
    if (!user) {
      setIsEnabled(false);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const evaluateFlag = async () => {
      try {
        setLoading(true);

        // Check cache
        const cacheKey = `${flagKey}:${user.id}`;
        const cached = flagCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          if (isMounted) {
            setIsEnabled(cached.value);
            setLoading(false);
          }
          return;
        }
        // Evaluar flag usando función RPC de Postgres
        const data = await SupabaseInstance.rpc<"is_feature_enabled">(
          "is_feature_enabled",
          {
            flag_key_param: flagKey,
            user_id_param: user.id,
          },
        );
        // Actualizar cache
        flagCache.set(cacheKey, { value: data, timestamp: Date.now() });
        if (isMounted) {
          setIsEnabled(data);
          setError(null);
        }
      } catch (err: unknown) {
        Logger.loggerError({
          section: "feature_flag_evaluate_individual_flag",
          message: err,
        });
        if (isMounted) {
          setError(err as Error);
          setIsEnabled(false); // Fail closed
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    evaluateFlag();

    return () => {
      isMounted = false;
    };
  }, [flagKey, user]);

  return { isEnabled, loading, error };
}

// Hook para evaluar múltiples flags
export function useFeatureFlags(
  flagKeys: (FeatureFlagKey | string)[],
): { flags: Record<string, boolean>; loading: boolean } {
  const { user } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const evaluateFlags = async () => {
      setLoading(true);
      try {
        const results: Record<string, boolean> = {};

        await Promise.all(
          flagKeys.map(async (key) => {
            // Check cache first
            const cacheKey = `${key}:${user.id}`;
            const cached = flagCache.get(cacheKey);

            if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
              results[key] = cached.value;
              return;
            }

            const data = await SupabaseInstance.rpc<"is_feature_enabled">(
              "is_feature_enabled",
              {
                flag_key_param: key,
                user_id_param: user.id,
              },
            );
            const value = data || false;
            flagCache.set(cacheKey, { value, timestamp: Date.now() });
            results[key] = value;
          }),
        );
        setFlags(results);
      } catch (error) {
        Logger.loggerError({
          section: "feature_flags_evaluate_flags",
          message: error,
        });
      } finally {
        setLoading(false);
      }
    };

    evaluateFlags();
  }, [flagKeys, user]);

  return { flags, loading };
}
